import _get from 'lodash/get';
import _set from 'lodash/set';
import { IParagraphStyle } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-styles.interface';
import DataTransform from '@src/slide/domain/ui-model/element-modeles/property/text/editor-data-model/data-transform';
import { isParaEmpty } from '@src/slide/domain/ui-model/element-modeles/property/text/utils/utils';
import { FONT_SCALE } from '../../constants';

/**
 * 1. 定位当前的para 2. 向前定位，找到和其不是同类型的、level比其小的、或者到第一个为止
 */
export function getLiteCharacterLevel({ index, character, level, paragraphs: paragraphList }): number {
    let characterIndex = 0;
    let i = index;
    if (_get(character, 'buAutoNum')) {
        const checkV = checkCharacterIndex(character, level, paragraphList, 'buAutoNum', i, characterIndex);
        characterIndex = checkV.characterIndex;
        i = checkV.i;
    }
    return characterIndex;
}

// eslint-disable-next-line complexity
export function checkCharacterIndex(character, level, paragraphList, value, i, characterIndex) {
    const tLevel = level || 0;
    // 自增的才需要
    const { type: tType, startAt: tStartAt } = character[value];
    while ((i -= 1) >= 0) {
        const tmpPara = paragraphList[i];
        const tmpCharacter = tmpPara.paragraphProps.character || tmpPara.paragraphCharacter;
        if (!tmpCharacter) break;
        const tmpLevel = tmpPara.paragraphProps.level || 0;

        // 1. 层级大于当前层级，直接跳过
        // 2. 层级小于当前层级，重新开始记数
        // 3. 层级等于当前层级，类型不同，开始重新计数
        // 4. 层级等于当前层级，类型相同且包含文字，计数加一
        if (tmpLevel > tLevel) continue;
        if (tmpLevel < tLevel) break;

        if (
            !tmpCharacter[value]
            // 只有类型和 startAt 都相同，才视为同一组进行递增
            || !(tmpCharacter[value].type === tType && tmpCharacter[value].startAt === tStartAt)
        ) {
            break;
        }
        // 只有同级且有文字才加
        // eslint-disable-next-line no-plusplus,no-param-reassign
        if (sameLevelAndParaNoEmpty(tmpLevel, tLevel, tmpPara)) characterIndex++;
    }
    return { i, characterIndex };
}

const sameLevelAndParaNoEmpty = (tmpLevel, tLevel, tmpPara) => tmpLevel === tLevel && !isParaEmpty(tmpPara);

export function mergeFirstTextRunAndParaStyle(para) {
    const firstTextRunStyle = para.textRuns.length > 0 ? para.textRuns[0].styles : {};
    return {
        ...para.paragraphProps,
        ...firstTextRunStyle,
    };
}

export function transformParagraphForCharacter(documentParagraph) {
    const paraStyle: IParagraphStyle = DataTransform.getOriginProps(documentParagraph);
    const textRuns = documentParagraph?.children.map((run) => {
        const styles = DataTransform.getOriginProps(run, 'raw-');
        return {
            text: run.text,
            styles,
        };
    });
    return {
        paragraphProps: paraStyle,
        textRuns,
    };
}

export function isOfficeBulletCharWithoutWingdings(firstBulletChar, character) {
    const NEED_SLICE_CHAR_WITH_TYPEFACE = ['l', 'n', 'u', 'p', 'ü', 'Ø']; // office自带的项目符号，只要没有Wingdings就不会重复
    return NEED_SLICE_CHAR_WITH_TYPEFACE.some(char => firstBulletChar.indexOf(char) > -1) && _get(character, 'buFont.typeface') !== 'Wingdings';
}

export function isOfficeDotBullet(firstBulletChar) {
    const NEED_SLICE_CHAR = ['•']; // 另一种office项目符号，无论有没有typeface都不重复
    return NEED_SLICE_CHAR.some(char => firstBulletChar.indexOf(char) > -1);
}

/**
 * 处理重复bulletChar逻辑
 * @param character
 */
export function sliceSameBulletChar(character) {
    if (character.character_bullet_char?.length > 1) {
        const firstBulletChar = character.character_bullet_char.slice(0, 1);
        if (isOfficeBulletCharWithoutWingdings(firstBulletChar, character)
            || isOfficeDotBullet(firstBulletChar)) {
            character.character_bullet_char = character.character_bullet_char.slice(0, 1);
        }
    }
}

/**
 *  项目符号合并sdk导入的属性
 * @param characterProps
 */
export function mergeBulletAttribute(characterProps) {
    const { character = {} } = characterProps;
    sliceSameBulletChar(character); // 筛掉office自身支持重复的bullet
    // buClrTx buFontTx 标签会删除原有的buClr buFont标签 如果需要支持字体颜色调整
    // 要注意删掉buClrTx buFontTx 还原 buClr buFont给sdk数据
    if (!character.buClrTx && character.buClr) {
        characterProps.color = character.buClr.color.srgb_color;
    }
    // 当存在buFontTx标签，项目符号使用字体需要跟textRun一致，
    if (character.buFontTx) {
        _set(character, 'buFont.typeface', characterProps.fontFamily);
    }

    if (!character.buSzTx) {
        if (character.buSzPts && !character.buSzPct) {
            // buSzPts标签仅显示 转化成 buSzPct 处理即可
            character.buSzPct = character.buSzPts * FONT_SCALE / characterProps.fontSize;
        }
    }
    return characterProps;
}


export function combineCharacter(paraCharacter) {
    const character = {
        ...paraCharacter,
    };

    // raw里存储的是非继承值的key，转换回来的时候根据raw里的key判断哪些是需要转换的
    character.raw = Object.keys(character);

    // 如果 buNone 为 1，表示不显示项目符号，就不用继承项目符号相关数据了
    if (character.buNone === 1) {
        return character;
    }
    return character;
}


export function convertCharacter(characterNode) {
    if (!characterNode) return null;
    const data = characterNode.character;

    const character = data?.raw ? data.raw.reduce((prev, curr) => ({
        ...prev,
        [curr]: data[curr],
    }), {}) : null;

    return character;
}
